import Background from "../assets/Background.jpg"
import Background2 from "../assets/Background2.jpg"
import Background3 from "../assets/Background3.jpg"

import {
    availabilityLabel,
    getHirePackages,
    getInsuranceIncluded,
    getVehicleBodyType,
    getVehicleColour,
    getVehicleDisplayName,
    getVehicleEngineCapacity,
    getVehicleFuel,
    getVehicleImageUrls,
    getVehicleLicensingAuthority,
    getVehicleMotData,
    getVehicleSeats,
    getVehicleTags,
    getVehicleTransmission,
} from "./vehicleHelpers"
import { formatApiDate, normalizeText } from "./textNormalize"

const FALLBACK_IMAGES = [Background, Background2, Background3]

const DEFAULT_INCLUDES_BASE = [
    "Private Hire rental (paid weekly)",
    "MOT & routine servicing",
    "Breakdown cover",
    "Road tax",
    "Local office support in Blaydon",
]

const platformFromTags = (tags = []) => {
    const platforms = new Set(["Uber", "Bolt"])
    if (tags.some((t) => /comfort/i.test(t))) platforms.add("Uber Comfort")
    if (tags.some((t) => /executive/i.test(t))) {
        platforms.delete("Uber Comfort")
        platforms.add("Uber Executive")
    }
    if (tags.some((t) => /electric/i.test(t))) platforms.add("Uber Green")
    return [...platforms]
}

const specFeatures = (vehicle) => {
    const mot = getVehicleMotData(vehicle)
    const core = [
        [getVehicleFuel(vehicle), getVehicleTransmission(vehicle)].filter(Boolean).join(" "),
        getVehicleSeats(vehicle) ? `${getVehicleSeats(vehicle)} seats` : null,
        getVehicleBodyType(vehicle),
        getVehicleColour(vehicle) ? `${getVehicleColour(vehicle)} exterior` : null,
        getVehicleEngineCapacity(vehicle) ? `${getVehicleEngineCapacity(vehicle)}cc` : null,
        "Licensed for Private Hire",
    ].filter(Boolean)

    const motAppointmentLine = (() => {
        if (mot?.motAppointmentAt) {
            const match = String(mot.motAppointmentAt).match(
                /^(\d{4}-\d{2}-\d{2})T([01]\d|2[0-3]):([0-5]\d)/
            )
            if (match) {
                return `MOT appointment ${formatApiDate(match[1])} at ${match[2]}:${match[3]}`
            }
        }
        if (mot?.motDueDate) {
            return `MOT due ${formatApiDate(mot.motDueDate)}`
        }
        return null
    })()

    const safety = [
        mot?.motStatus ? `MOT ${normalizeText(mot.motStatus)}` : null,
        motAppointmentLine,
        "ABS & stability control",
        "Multiple airbags",
    ].filter(Boolean)

    const interior = [
        "Air conditioning",
        "Comfortable passenger seating",
        getVehicleBodyType(vehicle) ? `${getVehicleBodyType(vehicle)} body style` : null,
    ].filter(Boolean)

    const tech = [
        "Bluetooth hands-free",
        "USB charging",
        "DAB radio",
    ]

    return { core, safety, interior, tech }
}

export const buildDefaultFeatures = specFeatures

const revealsVehicleAge = (value) => {
    const text = String(value || "").trim()
    if (!text) return false
    if (/^registered\b/i.test(text)) return true
    if (/\b\d{2}\s*plate\b/i.test(text)) return true
    if (/\bdate first registered\b/i.test(text)) return true
    return false
}

const scrubFeatureLists = (features) => {
    if (!features || typeof features !== "object") return features
    const next = {}
    Object.entries(features).forEach(([key, items]) => {
        if (!Array.isArray(items)) {
            next[key] = items
            return
        }
        next[key] = items.filter((item) => !revealsVehicleAge(item))
    })
    return next
}

const defaultSummary = (vehicle) => {
    const name = getVehicleDisplayName(vehicle)
    const fuel = getVehicleFuel(vehicle)
    return `${name} — a ${fuel ? `${fuel.toLowerCase()} ` : ""}Private Hire vehicle available to hire from our Blaydon office.`
}

const defaultDescription = (vehicle) => {
    const name = getVehicleDisplayName(vehicle)
    return `The ${name} is suited to licensed Private Hire drivers across Newcastle and the North East. Enquire for availability, deposit, and collection from our Blaydon office.`
}

export const enrichVehicle = (vehicle) => {
    if (!vehicle) return null

    const {
        vrm: _vrm,
        vin: _vin,
        driver: _driver,
        fetched_at: _fetchedAt,
        uk_vehicle_data: _ukVehicleData,
        vehicle_image_data: _vehicleImageData,
        ...publicVehicle
    } = vehicle

    const tags = getVehicleTags(vehicle)
    const packages = getHirePackages(vehicle)
    const insuranceIncluded = getInsuranceIncluded(vehicle)
    const licensingAuthority = getVehicleLicensingAuthority(vehicle)
    const images = getVehicleImageUrls(vehicle)
    const resolvedImages = images.length
        ? images
        : FALLBACK_IMAGES

    const defaultIncludes = () => {
        if (packages.withInsurance && packages.withoutInsurance) {
            return [
                ...DEFAULT_INCLUDES_BASE,
                "Optional fully comprehensive insurance — choose with or without",
            ]
        }
        if (packages.withInsurance) {
            return ["Fully comprehensive insurance", ...DEFAULT_INCLUDES_BASE]
        }
        return [...DEFAULT_INCLUDES_BASE, "Insurance arranged separately — ask our team"]
    }

    const includes = (vehicle.includes?.length ? vehicle.includes : defaultIncludes()).filter(
        (item) => !revealsVehicleAge(item)
    )

    const features = scrubFeatureLists(
        vehicle.features &&
            Object.values(vehicle.features).some((items) => Array.isArray(items) && items.length)
            ? vehicle.features
            : specFeatures(vehicle)
    )

    const depositFrom = (() => {
        const deposits = [packages.withInsurance?.deposit, packages.withoutInsurance?.deposit].filter(
            (n) => n != null
        )
        if (deposits.length) return Math.min(...deposits)
        return insuranceIncluded === false ? 200 : 250
    })()

    const priceFrom = (() => {
        const prices = [packages.withInsurance?.weekly, packages.withoutInsurance?.weekly].filter(
            (n) => n != null
        )
        return prices.length ? Math.min(...prices) : Number(vehicle.price) || 0
    })()

    return {
        ...publicVehicle,
        name: getVehicleDisplayName(vehicle),
        images: resolvedImages,
        coverUrl: resolvedImages[0],
        summary: vehicle.summary || defaultSummary(vehicle),
        description: vehicle.description || defaultDescription(vehicle),
        tags,
        licensingAuthority,
        insuranceIncluded,
        hirePackages: packages,
        price: priceFrom,
        price_with_insurance: packages.withInsurance?.weekly ?? null,
        price_without_insurance: packages.withoutInsurance?.weekly ?? null,
        availabilityLabel: availabilityLabel(vehicle.available, vehicle.available_from),
        features,
        platforms: platformFromTags(tags),
        hire: {
            deposit: depositFrom,
            deposit_with_insurance: packages.withInsurance?.deposit ?? null,
            deposit_without_insurance: packages.withoutInsurance?.deposit ?? null,
            minWeeks: vehicle.hire?.minWeeks ?? 1,
            mileageNote: vehicle.hire?.mileageNote || "Fair weekly mileage for Private Hire use",
            collection: vehicle.hire?.collection || "Collect from our Blaydon office",
        },
        includes,
        specs: [
            { label: "Make & model", value: getVehicleDisplayName(vehicle) },
            { label: "Colour", value: getVehicleColour(vehicle) || "—" },
            { label: "Fuel", value: getVehicleFuel(vehicle) || "—" },
            { label: "Transmission", value: getVehicleTransmission(vehicle) || "—" },
            { label: "Seats", value: getVehicleSeats(vehicle) || "—" },
            { label: "Body type", value: getVehicleBodyType(vehicle) || "—" },
            { label: "Engine", value: getVehicleEngineCapacity(vehicle) ? `${getVehicleEngineCapacity(vehicle)}cc` : "—" },
            { label: "Licensing authority", value: licensingAuthority || "—" },
            { label: "Availability", value: availabilityLabel(vehicle.available, vehicle.available_from) },
        ].filter((spec) => {
            if (["Year", "Year plate", "Registered", "Date first registered"].includes(spec.label)) {
                return false
            }
            return (
                spec.value !== "—" ||
                ["Make & model", "Fuel", "Transmission", "Availability", "Licensing authority"].includes(
                    spec.label
                )
            )
        }),
    }
}
